import { withoutHiddenUsers } from "@/lib/utils/display-email";
import { supabase } from "@/integrations/supabase/client";
import type {
  FeatureRow, MemberCompensation, MemberPermissions, RoleValue, SalaryPeriod, TeamMemberDetail, TeamMemberRow,
} from "./types";

type RpcResult<T> = { data: T | null; error: { message: string } | null };
type UntypedClient = {
  rpc: <T>(fn: string, args?: Record<string, unknown>) => PromiseLike<RpcResult<T>>;
  from: (table: string) => {
    select: (columns: string) => PromiseLike<RpcResult<unknown[]>>;
  };
};
// New RPCs are not in the generated types.ts (never edited by hand), so they go through this narrow client.
const db = supabase as unknown as UntypedClient;

export const TEAM_ERROR_CODES = new Set([
  "team_forbidden", "team_owner_locked", "team_self_locked", "team_not_member", "team_invalid_role",
  "team_invalid_action", "team_invalid_cpf", "team_invalid_phone", "team_invalid_dates", "team_invalid_name",
  "team_invalid_salary",
]);

export class TeamRpcError extends Error {
  constructor(public code: string) {
    super(code);
  }
}

export function teamErrorKey(error: unknown): string {
  const code = error instanceof TeamRpcError ? error.code : "";
  return TEAM_ERROR_CODES.has(code) ? `errors.${code}` : "errors.generic";
}

export async function rpc<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await db.rpc<T>(fn, args);
  if (error) throw new TeamRpcError(error.message);
  return data as T;
}

export type UpdateMemberInput = {
  userId: string; fullName: string; cpf: string | null; phone: string | null; birthDate: string | null;
  jobTitle: string | null; employmentType: string | null; hiredAt: string | null; terminatedAt: string | null;
  isVeterinarian: boolean; isGroomer: boolean;
};

export type SetSalaryInput = {
  userId: string; baseSalary: number | null; salaryPeriod: SalaryPeriod; salaryPayday: number | null;
};

export const teamApi = {
  listMembers: async () => withoutHiddenUsers(await rpc<TeamMemberRow[]>("team_list_members")),
  getMember: (userId: string) => rpc<TeamMemberDetail | null>("team_get_member", { p_user_id: userId }),
  getPermissions: (userId: string) => rpc<MemberPermissions>("team_get_member_permissions", { p_user_id: userId }),
  getCompensation: (userId: string) => rpc<MemberCompensation>("team_get_compensation", { p_user_id: userId }),
  updateMember: (i: UpdateMemberInput) =>
    rpc<void>("team_update_member", {
      p_user_id: i.userId, p_full_name: i.fullName, p_cpf: i.cpf, p_phone: i.phone, p_birth_date: i.birthDate,
      p_job_title: i.jobTitle, p_employment_type: i.employmentType, p_hired_at: i.hiredAt,
      p_terminated_at: i.terminatedAt, p_is_veterinarian: i.isVeterinarian, p_is_groomer: i.isGroomer,
    }),
  setRole: (userId: string, role: Exclude<RoleValue, "owner">) =>
    rpc<void>("team_set_role", { p_user_id: userId, p_role_value: role }),
  setPermission: (userId: string, feature: string, action: string, allowed: boolean) =>
    rpc<void>("team_set_permission", { p_user_id: userId, p_feature: feature, p_action: action, p_allowed: allowed }),
  resetPermissions: (userId: string) => rpc<void>("team_reset_permissions", { p_user_id: userId }),
  setSalary: (i: SetSalaryInput) =>
    rpc<void>("team_set_salary", {
      p_user_id: i.userId, p_base_salary: i.baseSalary, p_salary_period: i.salaryPeriod, p_salary_payday: i.salaryPayday,
    }),
  listFeatures: async (): Promise<FeatureRow[]> => {
    const { data, error } = await db.from("features").select("value, can_view, can_create, can_update, can_delete, modules(value)");
    if (error) throw new TeamRpcError(error.message);
    return (data as Array<Omit<FeatureRow, "module"> & { modules: { value: string } | null }>).map((f) => ({
      value: f.value, module: f.modules?.value ?? "core",
      can_view: !!f.can_view, can_create: !!f.can_create, can_update: !!f.can_update, can_delete: !!f.can_delete,
    }));
  },
};
